import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import React from 'react';
import type { Root } from 'react-dom/client';
import { createRoot  } from 'react-dom/client';
import { Provider } from 'react-redux';

import type { Company, ReferentMutation } from '../../../../@types/Company';
import { ReferentOverrideContext } from '../../../common/Variable/Variable.module';
import type { ModelId, VersionId } from './referentCampaignTemplates';
import { isCustomModelId, renderModelContent, versionLabels } from './referentCampaignTemplates';

/**
 * Build a filename slug from a referent's first/last name.
 */
const sanitizeReferentName = (referent: ReferentMutation): string => (
  `${referent.firstname}-${referent.lastname}`
    .toLowerCase()
    .replaceAll(/[^a-z0-9-]/g, '-')
    .replaceAll(/-+/g, '-')
);

/**
 * Tailwind 4 emits `oklch(...)` for its palette utilities. html2canvas (≤1.4)
 * cannot parse modern color functions and aborts with
 * "Attempting to parse an unsupported color function 'oklch'". To work around
 * this we walk the cloned subtree and rewrite every computed color-bearing
 * value to a plain `rgb()` string before rendering.
 *
 * The math follows the CSS Color Module Level 4 conversion path
 *  Oklch → Oklab → linear sRGB → sRGB.
 */
const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

const oklabToLinearSrgb = (L: number, a: number, b: number): [number, number, number] => {
  const lRoot = L + (0.3963377774 * a) + (0.2158037573 * b);
  const mRoot = L - (0.1055613458 * a) - (0.0638541728 * b);
  const sRoot = L - (0.0894841775 * a) - (1.291485548 * b);

  const l = lRoot * lRoot * lRoot;
  const m = mRoot * mRoot * mRoot;
  const s = sRoot * sRoot * sRoot;

  return [
    (4.0767416621 * l) - (3.3077115913 * m) + (0.2309699292 * s),
    (-1.2684380046 * l) + (2.6097574011 * m) - (0.3413193965 * s),
    (-0.0041960863 * l) - (0.7034186147 * m) + (1.707614701 * s),
  ];
};

const linearToSrgb = (channel: number): number => {
  const c = clamp01(channel);
  return c <= 0.0031308 ? (12.92 * c) : ((1.055 * (c ** (1 / 2.4))) - 0.055);
};

const oklchToRgb = (L: number, C: number, hDeg: number, alpha: number): string => {
  const h = (hDeg * Math.PI) / 180;
  const a = C * Math.cos(h);
  const b = C * Math.sin(h);
  const [lr, lg, lb] = oklabToLinearSrgb(L, a, b);
  const r = Math.round(linearToSrgb(lr) * 255);
  const g = Math.round(linearToSrgb(lg) * 255);
  const bl = Math.round(linearToSrgb(lb) * 255);
  if (alpha < 1) {
    return `rgba(${r}, ${g}, ${bl}, ${alpha})`;
  }
  return `rgb(${r}, ${g}, ${bl})`;
};

/**
 * Parse a CSS value's `oklch(...)` (or `oklch(... / alpha)`) occurrences and
 * replace them in-place with `rgb()`/`rgba()` equivalents. Non-oklch input is
 * returned unchanged.
 */
const replaceOklchInValue = (value: string): string => {
  if (!value.includes('oklch')) { return value; }

  return value.replaceAll(
    // L can be a number or a percentage; chroma is a positive number; hue is
    // an angle (number or deg). Optional " / alpha" tail. Commas or spaces.
    /oklch\(\s*([0-9.+\-%]+)\s+([0-9.+\-]+)\s+([0-9.+\-]+)(?:deg)?\s*(?:\/\s*([0-9.+\-%]+))?\s*\)/gi,
    (_match, lRaw: string, cRaw: string, hRaw: string, aRaw?: string) => {
      const L = lRaw.endsWith('%') ? Number.parseFloat(lRaw) / 100 : Number.parseFloat(lRaw);
      const C = Number.parseFloat(cRaw);
      const H = Number.parseFloat(hRaw);
      const alpha = aRaw === undefined
        ? 1
        : aRaw.endsWith('%')
          ? Number.parseFloat(aRaw) / 100
          : Number.parseFloat(aRaw);

      if ([L, C, H, alpha].some((n) => Number.isNaN(n))) {
        return 'rgb(0, 0, 0)';
      }

      return oklchToRgb(L, C, H, clamp01(alpha));
    },
  );
};

const OKLCH_PROPS = [
  'color',
  'background-color',
  'background-image',
  'border-top-color',
  'border-right-color',
  'border-bottom-color',
  'border-left-color',
  'outline-color',
  'text-decoration-color',
  'caret-color',
  'fill',
  'stroke',
  'column-rule-color',
  'box-shadow',
];

/**
 * Some Tailwind 4 utilities wrap colors in `color-mix(in oklch, …)`; even
 * after we've rewritten the inner `oklch(...)` to `rgb(...)`, html2canvas
 * still chokes on the outer `color-mix(...)`. Use a sacrificial 2D canvas as
 * a CSS color parser of last resort: assigning to `ctx.fillStyle` makes the
 * browser normalize the value to `rgb()`/`rgba()` when it can.
 */
let sharedCanvasCtx: CanvasRenderingContext2D | null = null;
const getCanvasCtx = (): CanvasRenderingContext2D | null => {
  if (sharedCanvasCtx) { return sharedCanvasCtx; }
  const canvas = document.createElement('canvas');
  sharedCanvasCtx = canvas.getContext('2d');
  return sharedCanvasCtx;
};

const flattenToRgb = (value: string): string => {
  const ctx = getCanvasCtx();
  if (!ctx) { return value; }
  try {
    ctx.fillStyle = '#000';
    ctx.fillStyle = value;
    const normalized = ctx.fillStyle;
    if (typeof normalized === 'string' && !normalized.includes('oklch') && !normalized.includes('color-mix')) {
      return normalized;
    }
  } catch {
    // ignored — fall through to original value
  }
  return value;
};

/**
 * Walk every element under `root` (inclusive) and force any color-bearing
 * computed property that resolves to `oklch(...)` (or wraps one in
 * `color-mix(...)`) back to a parseable `rgb()`/`rgba()` value via inline
 * styles. Safe to call multiple times — overridden values no longer contain
 * `oklch` so they short-circuit on subsequent visits.
 */
const neutralizeOklchColors = (root: HTMLElement): void => {
  const visit = (element: Element) => {
    if (!(element instanceof HTMLElement) && !(element instanceof SVGElement)) {
      return;
    }
    const computed = window.getComputedStyle(element);
    for (const property of OKLCH_PROPS) {
      const value = computed.getPropertyValue(property);
      if (!value || (!value.includes('oklch') && !value.includes('color-mix'))) {
        continue;
      }
      let replaced = replaceOklchInValue(value);
      if (replaced.includes('color-mix') || replaced.includes('oklch')) {
        replaced = flattenToRgb(replaced);
      }
      if (replaced !== value) {
        (element as HTMLElement).style.setProperty(property, replaced, 'important');
      }
    }
    for (const child of element.children) {
      visit(child);
    }
  };
  visit(root);
};

/**
 * Build a Redux Provider-compatible store stub that simply returns the current
 * company in `state.auth`. Used so `<Variable>` (which calls `useSelector`)
 * keeps working when the email template is rendered off-screen, without
 * having to plumb the real store from every caller.
 */
type MinimalStore = {
  getState: () => { auth: { company: Company | null } };
  subscribe: (listener: () => void) => () => void;
  dispatch: (action: unknown) => unknown;
};

const buildCompanyOnlyStore = (company: Company | null): MinimalStore => {
  const state = { auth: { company } };
  return {
    getState: () => state,
    subscribe: () => () => {},
    dispatch: (action: unknown) => action,
  };
};

/**
 * Render a referent-campaign email template into a hidden off-screen container
 * at A4 width, run html2canvas + jsPDF, and return the PDF blob.
 *
 * The provided `referent` is propagated via `ReferentOverrideContext` so the
 * `<Variable>` placeholders are substituted with this referent's data instead
 * of the company's first referent.
 */
const renderModelToPDFBlob = async(
  modelId: ModelId,
  versionId: VersionId,
  referent: ReferentMutation,
  company: Company,
): Promise<{ blob: Blob; filename: string }> => {
  const hostElement = document.createElement('div');
  hostElement.style.position = 'fixed';
  hostElement.style.left = '-10000px';
  hostElement.style.top = '0';
  hostElement.style.width = '210mm';
  hostElement.style.backgroundColor = 'white';
  hostElement.style.zIndex = '-1';
  document.body.appendChild(hostElement);

  const root: Root = createRoot(hostElement);
  // `<Variable>` reads `state.auth.company` via useSelector — we expose a
  // minimal Provider that returns the company without depending on the real
  // app store, so this utility is safely callable from anywhere.
  const store = buildCompanyOnlyStore(company);

  try {
    root.render(
      React.createElement(
        Provider,
        // @ts-expect-error -- our minimal store satisfies the subset of the
        // react-redux Store interface that Provider relies on at runtime.
        { store },
        React.createElement(
          ReferentOverrideContext.Provider,
          { value: referent },
          renderModelContent(modelId, versionId),
        ),
      ),
    );

    // Wait two animation frames so React commits + browser layout settles
    // before html2canvas captures the off-screen container.
    await new Promise<void>((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });

    // The on-screen preview shows an "email header" band above every
    // template body (either the actual "Objet : …" line, or an editorial
    // label like "MODELE N°1 BIS -- PRESENTATION"). The generated PDF is
    // intended to contain only the email body the syndic will paste into
    // their mail client, so we strip that header from the off-screen DOM
    // before snapshotting. Doing it here (rather than inside
    // renderModelContent) keeps the on-page UI untouched and applies
    // uniformly to every model/version variant.
    for (const header of hostElement.querySelectorAll('[data-email-header]')) {
      header.remove();
    }

    // Tailwind 4 uses oklch() for its palette utilities which html2canvas
    // cannot parse. Inline-override every color-bearing property with its
    // rgb() equivalent before snapshotting.
    neutralizeOklchColors(hostElement);

    const canvas = await html2canvas(hostElement, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      logging: false,
      scrollX: 0,
      scrollY: 0,
      backgroundColor: '#ffffff',
    });

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
    pdf.setProperties({
      title: `Référent Mutation Location - ${versionLabels[modelId]?.[versionId]?.label ?? ''}`,
      subject: 'Communication Référent Mutation Location',
      creator: 'COPRAVIE',
      author: 'COPRAVIE',
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const scaleX = pdfWidth / canvas.width;
    const scaleY = scaleX;

    const totalPages = Math.max(1, Math.ceil((canvas.height * scaleY * 0.98) / pdfHeight));
    for (let i = 0; i < totalPages; i++) {
      if (i > 0) { pdf.addPage(); }
      const sourceY = i * (pdfHeight / scaleY);
      const sourceHeight = Math.min(pdfHeight / scaleY, canvas.height - sourceY);

      const pageCanvas = document.createElement('canvas');
      pageCanvas.width = canvas.width;
      pageCanvas.height = sourceHeight;

      const ctx = pageCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(canvas, 0, sourceY, canvas.width, sourceHeight, 0, 0, canvas.width, sourceHeight);
      }
      const imgData = pageCanvas.toDataURL('image/jpeg', 1.0);
      pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, sourceHeight * scaleY);
    }

    const sanitizedName = sanitizeReferentName(referent);
    const filename = `referent-${modelId}-${versionId}-${sanitizedName}.pdf`;
    const blob = pdf.output('blob');

    return { blob, filename };
  } finally {
    root.unmount();
    if (hostElement.parentNode) {
      hostElement.parentNode.removeChild(hostElement);
    }
  }
};

/**
 * Generate PDFs for a list of referents sequentially. html2canvas captures
 * global DOM state, so concurrent calls would corrupt each other's output.
 * Filenames are deduplicated when two referents share the same first/last
 * name (an index suffix is appended to avoid silent overwrites in the zip).
 */
const renderPDFsForReferents = async(
  modelId: ModelId,
  versionId: VersionId,
  referents: ReferentMutation[],
  company: Company,
): Promise<Array<{ blob: Blob; filename: string }>> => {
  const results: Array<{ blob: Blob; filename: string }> = [];
  const used = new Set<string>();

  for (let idx = 0; idx < referents.length; idx++) {
    const referent = referents[idx];
    try {
      // eslint-disable-next-line no-await-in-loop -- sequential rendering is required (shared off-screen DOM)
      const result = await renderModelToPDFBlob(modelId, versionId, referent, company);
      let { filename } = result;
      if (used.has(filename)) {
        const dot = filename.lastIndexOf('.');
        const base = dot > 0 ? filename.slice(0, dot) : filename;
        const ext = dot > 0 ? filename.slice(dot) : '.pdf';
        filename = `${base}-${idx + 1}${ext}`;
      }
      used.add(filename);
      results.push({ blob: result.blob, filename });
    } catch (error) {
      console.error(
        `[Referent PDF] Failed to generate PDF for referent ${referent.firstname} ${referent.lastname}:`,
        error,
      );
    }
  }

  if (results.length === 0 && referents.length > 0) {
    throw new Error('All referent PDF generations failed. Check the browser console for details.');
  }

  return results;
};

export type DownloadReferentCampaignOptions = {
  modelId: ModelId;
  versionId: VersionId;
  referents: ReferentMutation[];
  company: Company;
  /**
   * Slug used in the ZIP filename when more than one referent is exported.
   * For example "campagne-1" yields `referent-mail-campagne-1-{modelId}-{versionId}.zip`.
   * When omitted, defaults to the model/version pair.
   */
  zipSlug?: string;
};

/**
 * Orchestrate the download of a referent-campaign email template as either a
 * single PDF (when one valid referent is configured) or a ZIP archive
 * containing one PDF per referent (when there are multiple).
 *
 * Throws if there is no valid referent configured.
 */
export const downloadReferentCampaignAsPDFOrZip = async({
  modelId,
  versionId,
  referents,
  company,
  zipSlug,
}: DownloadReferentCampaignOptions): Promise<void> => {
  if (isCustomModelId(modelId)) {
    throw new Error('Le téléchargement du Modèle 5 (Personnalisé) passe par l\'endpoint document dédié.');
  }
  if (referents.length === 0) {
    throw new Error('Aucun référent valide configuré. Renseignez au moins un référent pour générer le PDF.');
  }

  if (referents.length === 1) {
    const { blob, filename } = await renderModelToPDFBlob(modelId, versionId, referents[0], company);
    saveAs(blob, filename);
    return;
  }

  const pdfs = await renderPDFsForReferents(modelId, versionId, referents, company);
  const zip = new JSZip();
  const folder = zip.folder('Referent-Mails');
  for (const { blob, filename } of pdfs) {
    folder?.file(filename, blob);
  }

  const safeSlug = zipSlug ?? `${modelId}-${versionId}`;
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  saveAs(zipBlob, `referent-mail-${safeSlug}.zip`);
};
